// HR resources management — Employee Portal & App spec §15.
//
// HR adds the org-specific support resources employees see (EAP, occupational
// health, internal wellbeing contact, organisation-specific links) alongside
// the seeded platform defaults. The defaults are platform-managed and not shown
// here — this surface is the org's OWN rows only (`GET /api/hr/resources`).
// Mutations are HR_Admin only (the API gates them; HR_Viewer gets a read-only
// list).

import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  RESOURCE_CATEGORIES,
  RESOURCE_CATEGORY_LABELS,
  type CreateResourceRequest,
  type ResourceCard,
} from '@besideu/shared/resources';
import { api, ApiError } from '@/lib/api.ts';
import { useSession } from '@/lib/auth.ts';
import { Button } from '@/components/ui/Button.tsx';
import { Field, Select, TextInput } from '@/components/ui/Field.tsx';

const EMPTY_FORM: CreateResourceRequest = {
  category: 'internal',
  name: '',
  description: '',
  whenToUse: null,
  contactUrl: null,
  contactPhone: null,
  isExternal: false,
  isConfidential: false,
};

export function HrResources() {
  const session = useSession('hr-shell');
  const isAdmin = session.role === 'HR_Admin';
  const queryClient = useQueryClient();
  const resources = useQuery({
    queryKey: ['hr', 'resources'],
    queryFn: () => api.get<{ resources: ResourceCard[] }>('/api/hr/resources'),
  });

  // `editing` holds the id being edited (the form doubles as create/edit);
  // null = the form is in "add" mode.
  const [editingId, setEditingId] = useState<string | null>(null);
  const [form, setForm] = useState<CreateResourceRequest>(EMPTY_FORM);
  const [error, setError] = useState<string | null>(null);

  const invalidate = () => {
    void queryClient.invalidateQueries({ queryKey: ['hr', 'resources'] });
  };

  const save = useMutation({
    mutationFn: (input: CreateResourceRequest) =>
      editingId
        ? api.put<{ data: ResourceCard }>(`/api/hr/resources/${editingId}`, input)
        : api.post<{ data: ResourceCard }>('/api/hr/resources', input),
    onSuccess: () => {
      invalidate();
      setForm(EMPTY_FORM);
      setEditingId(null);
      setError(null);
    },
    onError: (e) =>
      setError(
        e instanceof ApiError && e.status === 400
          ? 'Check the fields and try again.'
          : 'Could not save.',
      ),
  });

  const remove = useMutation({
    mutationFn: (id: string) => api.del<{ ok: true }>(`/api/hr/resources/${id}`),
    onSuccess: invalidate,
  });

  function startEdit(r: ResourceCard) {
    setEditingId(r.id);
    setForm({
      category: r.category,
      name: r.name,
      description: r.description,
      whenToUse: r.whenToUse,
      contactUrl: r.contactUrl,
      contactPhone: r.contactPhone,
      isExternal: r.isExternal,
      isConfidential: r.isConfidential,
    });
    setError(null);
  }

  function cancelEdit() {
    setEditingId(null);
    setForm(EMPTY_FORM);
    setError(null);
  }

  const set = (patch: Partial<CreateResourceRequest>) => setForm((f) => ({ ...f, ...patch }));
  // Trim-then-null for the optional text fields so an empty box stores as null.
  const orNull = (v: string) => (v.trim() === '' ? null : v);

  return (
    <section className="space-y-8">
      <header>
        <h1 className="text-2xl font-semibold tracking-tight">Support resources</h1>
        <p className="mt-1 text-sm text-muted-foreground">
          Resources you add here appear for your employees alongside the national defaults
          (Samaritans, Cruse, Mind and others). Add your EAP, occupational health, or any internal
          contacts.
        </p>
      </header>

      {isAdmin && (
        <form
          className="space-y-4 rounded-lg border border-border p-6"
          onSubmit={(e) => {
            e.preventDefault();
            save.mutate(form);
          }}
        >
          <h2 className="text-lg font-semibold">
            {editingId ? 'Edit resource' : 'Add a resource'}
          </h2>

          <Field label="Category">
            {(id, descProps) => (
              <Select
                id={id}
                {...descProps}
                value={form.category}
                onChange={(e) =>
                  set({ category: e.target.value as CreateResourceRequest['category'] })
                }
              >
                {RESOURCE_CATEGORIES.map((c) => (
                  <option key={c} value={c}>
                    {RESOURCE_CATEGORY_LABELS[c]}
                  </option>
                ))}
              </Select>
            )}
          </Field>

          <Field label="Name">
            {(id, descProps) => (
              <TextInput
                id={id}
                {...descProps}
                required
                maxLength={120}
                value={form.name}
                onChange={(e) => set({ name: e.target.value })}
              />
            )}
          </Field>

          <Field label="Short description">
            {(id, descProps) => (
              <TextInput
                id={id}
                {...descProps}
                required
                maxLength={400}
                value={form.description}
                onChange={(e) => set({ description: e.target.value })}
              />
            )}
          </Field>

          <Field label="When to use it" hint="Optional">
            {(id, descProps) => (
              <TextInput
                id={id}
                {...descProps}
                maxLength={400}
                value={form.whenToUse ?? ''}
                onChange={(e) => set({ whenToUse: orNull(e.target.value) })}
              />
            )}
          </Field>

          <div className="grid gap-4 sm:grid-cols-2">
            <Field label="Contact link" hint="Optional">
              {(id, descProps) => (
                <TextInput
                  id={id}
                  {...descProps}
                  maxLength={500}
                  placeholder="https://…"
                  value={form.contactUrl ?? ''}
                  onChange={(e) => set({ contactUrl: orNull(e.target.value) })}
                />
              )}
            </Field>
            <Field label="Contact phone" hint="Optional">
              {(id, descProps) => (
                <TextInput
                  id={id}
                  {...descProps}
                  maxLength={60}
                  value={form.contactPhone ?? ''}
                  onChange={(e) => set({ contactPhone: orNull(e.target.value) })}
                />
              )}
            </Field>
          </div>

          <div className="grid gap-4 sm:grid-cols-2">
            <Field label="Internal or external">
              {(id, descProps) => (
                <Select
                  id={id}
                  {...descProps}
                  value={form.isExternal ? 'external' : 'internal'}
                  onChange={(e) => set({ isExternal: e.target.value === 'external' })}
                >
                  <option value="internal">Internal (within your organisation)</option>
                  <option value="external">External</option>
                </Select>
              )}
            </Field>
            <label className="flex items-center gap-2 self-end pb-3 text-sm">
              <input
                type="checkbox"
                checked={form.isConfidential}
                onChange={(e) => set({ isConfidential: e.target.checked })}
              />
              Confidential service
            </label>
          </div>

          <div className="flex items-center gap-3">
            <Button type="submit" disabled={save.isPending}>
              {save.isPending ? 'Saving…' : editingId ? 'Save changes' : 'Add resource'}
            </Button>
            {editingId && (
              <Button type="button" variant="ghost" onClick={cancelEdit}>
                Cancel
              </Button>
            )}
            {error && (
              <span className="text-sm text-traffic-red" role="alert">
                {error}
              </span>
            )}
          </div>
        </form>
      )}

      <div>
        <h2 className="text-lg font-semibold">Your organisation’s resources</h2>
        {resources.isPending ? (
          <p className="mt-2 text-sm text-muted-foreground">Loading…</p>
        ) : resources.error || !resources.data ? (
          <p className="mt-2 text-sm text-traffic-red">Could not load resources.</p>
        ) : resources.data.resources.length === 0 ? (
          <p className="mt-2 text-sm text-muted-foreground">
            No resources added yet. Employees still see the national defaults.
          </p>
        ) : (
          <ul className="mt-3 space-y-3">
            {resources.data.resources.map((r) => (
              <li key={r.id} className="rounded-md border border-border p-4">
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div>
                    <p className="font-medium">{r.name}</p>
                    <p className="text-xs text-muted-foreground">
                      {RESOURCE_CATEGORY_LABELS[r.category]} ·{' '}
                      {r.isExternal ? 'External' : 'Internal'}
                      {r.isConfidential ? ' · Confidential' : ''}
                    </p>
                  </div>
                  {isAdmin && (
                    <div className="flex shrink-0 gap-2">
                      <Button type="button" variant="ghost" onClick={() => startEdit(r)}>
                        Edit
                      </Button>
                      <Button
                        type="button"
                        variant="ghost"
                        disabled={remove.isPending}
                        onClick={() => remove.mutate(r.id)}
                      >
                        Delete
                      </Button>
                    </div>
                  )}
                </div>
                <p className="mt-1 text-sm text-foreground/80">{r.description}</p>
              </li>
            ))}
          </ul>
        )}
      </div>
    </section>
  );
}
